import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { AcademicYear } from '@repo/shared';
import { api } from '../lib/api';

interface YearState {
  years: AcademicYear[];
  year: AcademicYear | null;
  setYearId: (id: string) => void;
}

const YearContext = createContext<YearState>({ years: [], year: null, setYearId: () => undefined });
const KEY = `sol-year:${typeof location === 'undefined' ? '' : location.hostname}`;

/** Academic year selected in the top bar (defaults to the tenant's current year), remembered per host. */
export function YearProvider({ children }: { children: ReactNode }) {
  const { data: years = [] } = useQuery({
    queryKey: ['academic-years'],
    queryFn: () => api<AcademicYear[]>('/academic-years'),
    staleTime: 10 * 60_000,
  });
  const [selected, setSelected] = useState<string | null>(() => {
    try {
      return localStorage.getItem(KEY);
    } catch {
      return null;
    }
  });
  const value = useMemo<YearState>(() => {
    const year =
      years.find((y) => y.id === selected) ?? years.find((y) => y.status === 'current') ?? years[0] ?? null;
    return {
      years,
      year,
      setYearId: (id) => {
        setSelected(id);
        try {
          localStorage.setItem(KEY, id);
        } catch {
          // ignore
        }
      },
    };
  }, [years, selected]);
  return <YearContext.Provider value={value}>{children}</YearContext.Provider>;
}

export const useYear = () => useContext(YearContext);
